/**
 * WHAT THE PIECE IS ABOUT THIS WEEK. D-EZ-70.
 *
 * Howard, 20 Aug 2026: *"The end of race newspaper article is great. Its
 * literally the perfect implementation. I wonder though is there anything we can
 * do to amke them more unique, more varied but retain their awesomeness?"*
 *
 * ===========================================================================
 * THE SAMENESS WAS NOT IN THE PROSE. IT WAS IN THE ASSIGNMENT.
 * ===========================================================================
 *
 * The model was handed one brief, every race, all year: lead, what happened, the
 * championship, a close. Written well thirty-six times — which is what he liked
 * — but a desk that files the same four-part shape every Monday is a desk with
 * one idea, and no amount of good sentences hides that.
 *
 * ⚠ THE NO-KEY EDITION ALREADY DID THIS AND NOBODY TOLD THE MODEL.
 * `fallbackPaper.leadAngle` has ranked its opening since D-EZ-50 — a first win
 * outranks a points lead changing hands, which outranks the favourite winning
 * again — precisely so that "two consecutive editions do not open the same way
 * unless two consecutive races were the same race". The written-here article was
 * more varied in shape than the bought one. This file is that idea taken
 * seriously and given to both.
 *
 * ===========================================================================
 * HOW AN ANGLE IS CHOSEN
 * ===========================================================================
 *
 * By reading the facts, in a fixed order of editorial priority, and taking the
 * first that fits. Not at random — the same race produces the same angle for
 * ever, which is the same commitment every other part of this engine makes and
 * the reason a re-read of race 14 is the article you remember.
 *
 * ⚠ EXACTLY ONE ANGLE. The temptation is to hand over three and let the writer
 * pick; that produces a piece with three spines and no shape, which is what the
 * single fixed brief already was. An editor decides what the story is, and a
 * story that is about everything is about nothing.
 *
 * ⚠ AND IT ONLY MOVES THE EMPHASIS. The angle says what leads and what the body
 * is built around. It never adds a fact, never asks for a claim the ledger does
 * not carry, and never relaxes a single rule in the gate — every angle below is
 * a way of ORDERING material the facts already contain.
 */

import type { PaperFacts } from './paperFacts.ts';

export type RaceAngle =
  /** A man's first win. The biggest story a race can hand you. */
  | 'first-win'
  /** The championship was settled, or its lead changed hands. */
  | 'title-turn'
  /** A multi-car wreck was the afternoon's defining event. */
  | 'the-big-one'
  /** It was decided late, and by two cars racing each other. */
  | 'duel'
  /** Somebody came from a long way back. */
  | 'charge'
  /** The winner started deep in the field. */
  | 'from-deep'
  /** The field fell apart; finishing was the achievement. */
  | 'attrition'
  /** Led most of the way and was never troubled. */
  | 'runaway'
  /** Another one for a man who keeps winning. */
  | 'streak'
  /** Nothing stands out. Report the race. */
  | 'routine';

export type SeasonAngle =
  /** Decided by a handful of points. */
  | 'close-title'
  /** Decided long before the last race. */
  | 'runaway-title'
  /** A first championship. */
  | 'first-title'
  /** A title on the record of a man who has them already. */
  | 'another-title'
  | 'routine-season';

export type PaperAngle = RaceAngle | SeasonAngle;

export const RACE_ANGLES: readonly RaceAngle[] = [
  'first-win',
  'title-turn',
  'the-big-one',
  'duel',
  'charge',
  'from-deep',
  'attrition',
  'runaway',
  'streak',
  'routine',
];

export const SEASON_ANGLES: readonly SeasonAngle[] = [
  'close-title',
  'runaway-title',
  'first-title',
  'another-title',
  'routine-season',
];

export function isPaperAngle(value: unknown): value is PaperAngle {
  return (
    typeof value === 'string' &&
    ((RACE_ANGLES as readonly string[]).includes(value) || (SEASON_ANGLES as readonly string[]).includes(value))
  );
}

// ---------------------------------------------------------------------------
// The thresholds, in one place and named
// ---------------------------------------------------------------------------

/**
 * ⚠ EVERY NUMBER HERE IS AN EDITORIAL JUDGEMENT AND IS WRITTEN DOWN AS ONE.
 *
 * They are not tuned against anything; they are what a racing weekly would call
 * a story. Buried in an `if` they would read as arithmetic, and the next person
 * would "fix" one without knowing they were changing what the paper thinks is
 * news.
 */
const THRESHOLDS = {
  /** A points lead this close, this late, is the story whoever won. */
  titleMargin: 25,
  /** …but only once the year is far enough along for it to mean anything. */
  titleLateFrom: 0.6,
  /**
   * Cars out of the race before it is attrition rather than bad luck.
   * ⚠ MEASURED, not guessed: a settled season of seed 424242 runs 2-5 out on an
   * ordinary afternoon, so 8 never fired and 5 would have fired every week.
   */
  attrition: 7,
  /**
   * Places gained before a drive is a charge.
   * ⚠ MEASURED: 21, 24, 33, 36, 33, 25, 38, 21 across the first eight races of
   * that season. In a 43-car field where the back half shuffles every turn,
   * gaining twenty places is Tuesday. Thirty-two is a drive.
   */
  charge: 32,
  /** A winner starting this deep is a story by itself. */
  fromDeep: 15,
  /**
   * Turns led, as a share of the race, before it is a runaway.
   * ⚠ MEASURED: winners led 4-19 turns of 28-50 on ordinary afternoons and 42 of
   * 49 on the one that was a runaway. The gap is wide and this sits in it.
   */
  runawayShare: 0.55,
  /** Wins on the year before it is a streak worth leading on. */
  streak: 3,
  /** A duel or pass inside this share of the race is a late one. */
  lateShare: 0.85,
  /** Season points margin below which a championship was close. */
  closeTitle: 60,
  /** …and above which it was a runaway. */
  runawayTitle: 200,
} as const;

// ---------------------------------------------------------------------------

/**
 * The angle for this edition. Ranked, and the ranking IS the editorial voice of
 * the paper — change the order and you change what it thinks matters.
 */
export function angleFor(facts: PaperFacts): PaperAngle {
  // D-EZ-102 — the winter edition is written about the same settled season.
  return facts.edition === 'race' ? raceAngle(facts) : seasonAngle(facts);
}

function seasonAngle(facts: PaperFacts): SeasonAngle {
  const c = facts.championship;
  const champion = c.championName ?? c.leaderName;
  // ⚠ `championships` is on `career`, not on `championship`. Two fields one word
  // apart, and the wrong one typechecks as `undefined` at runtime in JS.
  const titles = facts.career.championships.find((t) => t.name === champion)?.titles ?? 0;
  // ⚠ `championships` counts titles INCLUDING this one, so "first" is exactly
  // one. Reading it as zero would call every champion a first-time champion.
  if (titles <= 1) return 'first-title';
  if (c.marginToSecond > 0 && c.marginToSecond <= THRESHOLDS.closeTitle) return 'close-title';
  if (c.marginToSecond >= THRESHOLDS.runawayTitle) return 'runaway-title';
  if (titles > 1) return 'another-title';
  return 'routine-season';
}

function raceAngle(facts: PaperFacts): RaceAngle {
  const race = facts.race;
  if (!race) return 'routine';
  const turns = Math.max(1, lastTurn(facts));

  // 1. A first win. Nothing outranks it.
  if (race.winner.firstCareerWin) return 'first-win';

  // 2. The championship, when the championship is the news — a lead that
  //    changed hands, or one close enough to matter this late in the year.
  const leaderMoved = facts.championship.top5[0]?.moved ?? null;
  const late = facts.racesRun / Math.max(1, facts.racesInSeason) >= THRESHOLDS.titleLateFrom;
  if (leaderMoved !== null && leaderMoved > 0) return 'title-turn';
  if (late && facts.championship.marginToSecond > 0 && facts.championship.marginToSecond <= THRESHOLDS.titleMargin) {
    return 'title-turn';
  }

  // 3. The big one. A wreck that took a chunk of the field is the afternoon.
  if (race.incidents.some((i) => i.kind === 'the big one')) return 'the-big-one';

  /*
    4. Decided late, by two cars racing each other.

    ⚠ `'the finish'` IS DELIBERATELY NOT IN HERE. It was, for one measurement:
    the flag falling is tagged as an incident and it happens at the end of every
    single race, so "a late finish incident" made twelve of thirty-six editions a
    duel piece. An angle that fires a third of the time is not an angle, it is
    the new default with a more interesting name.
  */
  const lateFrom = turns * THRESHOLDS.lateShare;
  if (race.incidents.some((i) => i.turn >= lateFrom && i.kind === 'a duel')) return 'duel';

  /*
    5. Nobody else got a look in.

    ⚠ AHEAD OF THE CHARGE, and the order was corrected by measurement. A man who
    led forty-two of forty-nine turns has given you a piece about domination; on
    the same afternoon somebody also gained thirty-six places, because in a
    43-car field somebody always does. Ranking the charge first meant the one
    genuine runaway of the season was filed as a hard-luck-to-hero story.
  */
  if (race.winner.ledMost && race.winner.turnsLed >= turns * THRESHOLDS.runawayShare) return 'runaway';

  // 6. Somebody came from a long way back — whoever it was.
  if ((race.biggestMover?.gained ?? 0) >= THRESHOLDS.charge) return 'charge';
  if ((race.winner.started ?? 0) >= THRESHOLDS.fromDeep) return 'from-deep';

  // 7. The field fell apart.
  if (race.fellOut.length >= THRESHOLDS.attrition) return 'attrition';

  // 8. A man who keeps winning won again.
  if (race.winner.seasonWins >= THRESHOLDS.streak) return 'streak';

  return 'routine';
}

/**
 * How long the race was, in turns.
 *
 * ⚠ READ OFF THE INCIDENTS, NOT ASSUMED. The facts do not carry a turn count and
 * tracks run from 22 turns to 60, so a fixed number would call a Watkins Glen
 * pass late and a Charlotte one early. With no incidents at all there is nothing
 * to be late in, and the shares below simply never fire.
 */
function lastTurn(facts: PaperFacts): number {
  let max = 0;
  for (const i of facts.race?.incidents ?? []) if (i.turn > max) max = i.turn;
  return max;
}

// ---------------------------------------------------------------------------
// What the angle asks the writer for
// ---------------------------------------------------------------------------

/**
 * ⚠ THESE ARE ASSIGNMENTS, NOT STYLES.
 *
 * Each one says what the piece is about and what its body is built from. None
 * of them touches the voice — the paper has one voice, plain American newspaper
 * prose of the period, and six different voices would be six caricatures. What
 * changes week to week is what the desk thinks the story is, which is what
 * changes at a real paper too.
 *
 * ⚠ AND NONE OF THEM ASKS FOR A FACT. Read them again if one ever seems to: an
 * assignment that requires something the ledger does not carry produces either a
 * refused article or an invented one, and both are worse than the routine shape.
 */
export const ANGLE_BRIEF: Record<PaperAngle, string> = {
  'first-win': `THE ANGLE: a first career win. Lead on the man, not the race — how long
he has been at it, how many starts it took. The afternoon and the championship
still get their paragraphs, but this piece belongs to him.`,

  'title-turn': `THE ANGLE: the championship. The race is reported, but the spine of
this piece is the points — who leads now, who led before, what the margin is and
what is left to run. Put the championship paragraphs high, not at the end.`,

  'the-big-one': `THE ANGLE: a multi-car wreck. Lead with it: when it happened, who was
collected, what it did to the running order. Build the middle of the piece around
what the race became afterwards. The winner is reported plainly — on an afternoon
like this, winning is the smaller story.`,

  duel: `THE ANGLE: it was decided late, and by two cars racing each other. Lead on
the closing miles and stay there for the first two paragraphs — who had it, who
took it, what it cost. Everything earlier in the day is compressed into one
paragraph that sets it up.`,

  charge: `THE ANGLE: a drive through the field. Lead on the man who came from a long
way back and follow him forward through the afternoon; the incidents are his
traffic. Report the winner properly, but this piece is about the charge.`,

  'from-deep': `THE ANGLE: the winner started deep. Lead on the distance he made up and
tell the afternoon as the story of him getting to the front — where he was, what
he got past, when he arrived.`,

  attrition: `THE ANGLE: the field fell apart. Lead on the wreckage — how many did not
finish and what took them out — and treat the result as a survival. Group the
mechanical failures and the wrecks separately if the facts support it. Name the
men who were running at the end.`,

  runaway: `THE ANGLE: a runaway. He led it, and the piece must be honest that the win
was never in doubt — so the interest is behind him. Lead on the domination in one
short paragraph, then spend the body on the race that was actually going on for
the other positions.`,

  streak: `THE ANGLE: a man who keeps winning. Lead on the run he is on and what it is
doing to the season and to his career numbers. Then the afternoon, then what it
means for the championship.`,

  routine: `THE ANGLE: no single thing dominates, so report the race straight. Lead with
the most interesting true thing available and let the afternoon carry the piece.`,

  'first-title': `THE ANGLE: a first championship. Lead on the man and the length of the
road to it. The season is told as how he got there.`,

  'close-title': `THE ANGLE: a championship decided by a handful of points. Lead on how
close it was and on where it was actually won and lost across the year.`,

  'runaway-title': `THE ANGLE: a championship decided long before the last race. Say so
plainly and early, then spend the piece on how it got that way and on the season
the rest of them had.`,

  'another-title': `THE ANGLE: a title added to a record that already had some. Lead on
the man's place in the record book and what this one does to it.`,

  'routine-season': `THE ANGLE: report the year straight — the championship, the shape of
the season, and where it leaves people.`,
};
